import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, Upload } from "lucide-react";

const cred = { withCredentials: true };
const COUNT_LABELS = [["update", "To update"], ["add", "To add"], ["unchanged", "Unchanged"], ["protected", "Played (locked)"], ["review", "Needs review"]];

const Fixture = ({ r }) => (
  <span><b>#{r.match_number}</b> · Wk {r.week} · {r.home_name || r.home} v {r.away_name || r.away}</span>
);

const Changes = ({ changes }) => (
  <span className="text-xs text-gray-500">
    {Object.entries(changes || {}).map(([k, v]) => <span key={k} className="mr-3">{k.replace("match_", "")}: <s>{String(v.from ?? "—")}</s> → <b className="text-[#09090b]">{String(v.to)}</b></span>)}
  </span>
);

const Rows = ({ title, rows, render, testId, tone = "" }) => rows.length > 0 && (
  <details className="border border-gray-200 bg-white" data-testid={testId}>
    <summary className={`px-4 py-2 cursor-pointer text-sm font-semibold ${tone}`}>{title} ({rows.length})</summary>
    <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto text-sm">
      {rows.map((r, i) => <li key={i} className="px-4 py-2 flex flex-col gap-0.5">{render(r)}</li>)}
    </ul>
  </details>
);

const Division = ({ d }) => {
  const anomalies = [...d.update, ...d.add, ...d.protected, ...d.review].filter((r) => r.source_anomaly);
  return (
    <section className="space-y-3" data-testid={`schedule-division-${d.key}`}>
      <div className="flex items-end justify-between">
        <h2 className="font-heading font-black uppercase text-xl">{d.competition || d.key}</h2>
        <span className="text-xs text-gray-500">{d.source_fixtures} fixtures in source · {d.counts?.existing_in_db ?? 0} in database</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {COUNT_LABELS.map(([k, label]) => (
          <div key={k} className={`p-3 border bg-white ${k === "review" && d.counts?.[k] ? "border-orange-400" : "border-gray-200"}`} data-testid={`count-${d.key}-${k}`}>
            <p className="font-heading font-black text-2xl">{d.counts?.[k] ?? 0}</p>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
          </div>
        ))}
      </div>
      {Object.keys(d.aliases_used || {}).length > 0 && (
        <p className="text-xs bg-blue-50 text-blue-800 px-3 py-2" data-testid={`aliases-${d.key}`}>
          Name matches to confirm: {Object.entries(d.aliases_used).map(([s, t]) => `"${s}" → ${t}`).join(" · ")}
        </p>
      )}
      <Rows title="Source anomalies for ABFA confirmation" rows={anomalies} testId={`anomalies-${d.key}`} tone="text-orange-700"
        render={(r) => <><Fixture r={r} /><span className="text-xs text-orange-700">{r.date} · {r.source_anomaly}</span></>} />
      <Rows title="Needs review (not applied)" rows={d.review} testId={`review-${d.key}`} tone="text-orange-700"
        render={(r) => <><Fixture r={r} /><span className="text-xs text-orange-700">{r.reason}</span></>} />
      <Rows title="Fixtures to update" rows={d.update} testId={`updates-${d.key}`}
        render={(r) => <><Fixture r={r} /><Changes changes={r.changes} /></>} />
      <Rows title="New fixtures to add" rows={d.add} testId={`adds-${d.key}`}
        render={(r) => <><Fixture r={r} /><span className="text-xs text-gray-500">{r.date} {r.time} · {r.venue}</span></>} />
      <Rows title="Played matches (never modified)" rows={d.protected} testId={`protected-${d.key}`}
        render={(r) => <><Fixture r={r} />{Object.keys(r.differences || {}).length > 0 && <span className="text-xs text-gray-500">Source differs: <Changes changes={r.differences} /></span>}</>} />
    </section>
  );
};

const AdminScheduleImport = () => {
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setPlan(null);
    axios.get(`${API}/admin/schedule-import/preview`, cred).then((r) => setPlan(r.data)).catch(() => toast.error("Could not build dry-run report"));
    axios.get(`${API}/admin/schedule-import/history`, cred).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const totals = plan?.divisions.reduce((t, d) => ({ update: t.update + (d.counts?.update || 0), add: t.add + (d.counts?.add || 0), review: t.review + (d.counts?.review || 0) }), { update: 0, add: 0, review: 0 });

  const apply = async () => {
    if (!window.confirm(`Apply ${totals.update} updates and add ${totals.add} fixtures? Played matches and flagged rows are not touched.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/schedule-import/apply`, { confirm: true }, cred);
      toast.success("Schedule applied");
      console.info(r.data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Official Schedule Import">
      <div className="space-y-8" data-testid="admin-schedule-import">
        <div className="bg-white border border-gray-200 p-5 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[260px]">
            <p className="font-semibold">ABFA 2026/27 revised schedules: APL Round 1 · First Division (Nov 2026) · Second Division (Oct 30)</p>
            <p className="text-xs text-gray-500 mt-1">Dry run. Nothing changes until you apply. Played or live matches, scores and events are never modified. Re-running cannot create duplicates.</p>
          </div>
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="refresh-plan-btn"><RefreshCw className="w-4 h-4 mr-2" /> Refresh report</Button>
          <Button className="rounded-none bg-[#FFC72C] text-black hover:bg-[#E6B325]" disabled={!plan || busy || (totals.update + totals.add === 0)} onClick={apply} data-testid="apply-schedule-btn">
            <Upload className="w-4 h-4 mr-2" /> {busy ? "Applying…" : `Apply ${totals ? totals.update + totals.add : 0} changes`}
          </Button>
        </div>
        {totals?.review > 0 && <p className="text-sm text-orange-700 flex items-center gap-2" data-testid="review-warning"><AlertTriangle className="w-4 h-4" /> {totals.review} fixture(s) need review and will be skipped.</p>}
        {!plan ? <p className="text-gray-500">Building report…</p> : plan.divisions.map((d) => <Division key={d.key} d={d} />)}
        {history.length > 0 && (
          <div className="bg-white border border-gray-200 p-4 text-sm" data-testid="schedule-import-history">
            <p className="font-heading font-bold uppercase mb-2">Applied imports</p>
            {history.map((h) => (
              <p key={h.at} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#009E60]" /> {new Date(h.at).toLocaleString()} · {h.by} · {Object.entries(h.applied).map(([k, v]) => `${k}: ${v.updated} updated, ${v.added} added`).join(" · ")}</p>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminScheduleImport;
